import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { MapPin, Users, Trophy, Calendar, ArrowRight, Clock, Banknote, Smartphone, GraduationCap, ExternalLink } from "lucide-react";
import { TEST_IDS } from "@/constants/testIds";
import { motion } from "framer-motion";
import { useLanguage } from "@/contexts/LanguageContext";
import api from "@/lib/api";

const HERO_IMG = "https://static.prod-images.emergentagent.com/jobs/73c5a229-543f-4a8b-9f5c-8850c8da746d/images/8d7bce9c3ea13f34f7b9d7fbe7ee854fa5cdc96e0bbc114eba9b2ca916a4beea.png";

export default function Home() {
  const { t } = useLanguage();
  const [club, setClub] = useState(null);

  useEffect(() => {
    api.get("/club").then(r => setClub(r.data)).catch(() => {});
  }, []);

  const features = [
    { icon: Calendar, title: t("home.feat1"), desc: t("home.feat1_desc"), color: "text-teal", bg: "rgba(0,109,119,0.1)" },
    { icon: Banknote, title: t("home.feat2"), desc: t("home.feat2_desc"), color: "text-coral", bg: "rgba(31,95,74,0.1)" },
    { icon: Users, title: t("home.feat3"), desc: t("home.feat3_desc"), color: "text-golden", bg: "rgba(255,209,102,0.1)" },
  ];

  const courtPrice = club?.court_price || 1000;

  return (
    <div>
      <section data-testid={TEST_IDS.HERO_SECTION} className="relative min-h-[88vh] flex items-center">
        <div className="absolute inset-0">
          <img src={HERO_IMG} alt="Tamarin, Mauritius" className="w-full h-full object-cover" />
          <div className="absolute inset-0 hero-overlay" />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-8 lg:px-12 w-full">
          <motion.div initial={{ opacity: 0, y: 40 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }} className="max-w-2xl">
            <h1 className="font-heading font-black text-4xl sm:text-5xl lg:text-6xl tracking-tighter leading-[1.05] mb-6 text-gray-900 dark:text-white dark:drop-shadow-lg [text-shadow:_0_2px_8px_rgba(255,255,255,0.5)] dark:[text-shadow:_0_2px_8px_rgba(0,0,0,0.5)]">
              {t("home.title1")} <span className="text-golden">{t("home.title2")}</span>
            </h1>
            <div className="flex flex-wrap gap-3 mt-2">
              <Link to="/book">
                <Button data-testid={TEST_IDS.HERO_CTA} className="rounded-full bg-coral hover:bg-coral-600 text-white px-8 py-6 text-base font-semibold shadow-lg shadow-coral/25 transition-all hover:-translate-y-0.5">
                  {t("home.cta")} <ArrowRight size={18} className="ml-2" />
                </Button>
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Quick facts strip */}
      <section className="-mt-10 relative z-20 px-4 sm:px-8">
        <div className="max-w-5xl mx-auto grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="quick-facts">
          {[
            { icon: Banknote, label: t("home.fact_price"), value: `Rs ${courtPrice.toLocaleString()}`, sub: t("home.fact_price_sub", { price: club?.price_per_slot || courtPrice / 4 }) },
            { icon: Clock, label: t("home.fact_hours"), value: (club?.opening_hours || "08:00-18:00").replace("-", " – "), sub: t("home.fact_hours_sub") },
            { icon: MapPin, label: t("home.fact_location"), value: "Tamarin", sub: club?.address || "8 Ave de la Montagne, Morc Blackrock", href: club?.maps_url },
            { icon: Smartphone, label: t("home.fact_payment"), value: t("home.fact_payment_val"), sub: t("home.fact_payment_sub") },
          ].map((f, i) => (
            <motion.div key={f.label} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 + i * 0.08 }}
              className="rounded-2xl bg-card/95 backdrop-blur border border-border/50 p-4 shadow-lg">
              <div className="flex items-center gap-2 text-muted-foreground text-[11px] uppercase tracking-wider mb-1"><f.icon size={13} className="text-golden" /> {f.label}</div>
              <p className="font-heading font-black text-lg leading-tight">{f.value}</p>
              {f.href ? (
                <a href={f.href} target="_blank" rel="noreferrer" className="text-[11px] text-teal hover:underline flex items-center gap-1 truncate">{f.sub} <ExternalLink size={9} /></a>
              ) : <p className="text-[11px] text-muted-foreground truncate">{f.sub}</p>}
            </motion.div>
          ))}
        </div>
      </section>

      {/* How it works */}
      <section className="py-20 sm:py-24 px-4 sm:px-8">
        <div className="max-w-7xl mx-auto">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="mb-12 max-w-xl">
            <h2 className="font-heading font-bold text-2xl sm:text-3xl lg:text-4xl tracking-tight mb-3">{t("home.features_title")}</h2>
            <p className="text-muted-foreground text-base sm:text-lg">{t("home.features_subtitle")}</p>
          </motion.div>
          <div className="max-w-3xl space-y-4" data-testid="how-it-works">
            {features.map((f, i) => (
              <motion.div key={f.title} initial={{ opacity: 0, x: -20 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.1 }}
                className="card-lift flex items-start gap-5 p-6 rounded-2xl bg-card border border-border/50 backdrop-blur-sm">
                <div className={`w-12 h-12 rounded-xl flex items-center justify-center shrink-0 ${f.color}`} style={{ backgroundColor: f.bg }}><f.icon size={22} /></div>
                <div>
                  <h3 className="font-heading font-bold text-lg mb-1">{f.title}</h3>
                  <p className="text-muted-foreground text-sm leading-relaxed">{f.desc}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Tournament + Coaching teasers */}
      <section className="pb-20 px-4 sm:px-8">
        <div className="max-w-7xl mx-auto">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="mb-10 max-w-xl">
            <h2 className="font-heading font-bold text-2xl sm:text-3xl lg:text-4xl tracking-tight mb-3">{t("home.more_title")}</h2>
            <p className="text-muted-foreground text-base sm:text-lg">{t("home.more_desc")}</p>
          </motion.div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="rounded-3xl p-8 border border-golden/25 bg-gradient-to-br from-golden/10 to-transparent" data-testid="home-tournament-teaser">
            <div className="flex items-center gap-2 text-golden text-xs uppercase tracking-wider font-semibold mb-3"><Trophy size={14} /> {t("nav.tournaments")}</div>
            <h3 className="font-heading font-black text-2xl mb-1">{t("home.tournaments_title")}</h3>
            <p className="text-sm text-muted-foreground mb-4">{t("home.tournaments_desc")}</p>
            <span className="inline-flex items-center rounded-full bg-golden/15 text-golden border border-golden/30 px-4 py-1.5 text-sm font-semibold">{t("home.coming_soon")}</span>
          </div>
          <div className="rounded-3xl p-8 border border-teal/25 bg-gradient-to-br from-teal/10 to-transparent" data-testid="home-coaching-teaser">
            <div className="flex items-center gap-2 text-teal text-xs uppercase tracking-wider font-semibold mb-3"><GraduationCap size={14} /> {t("nav.coaching")}</div>
            <h3 className="font-heading font-black text-2xl mb-1">{t("home.coaching_title")}</h3>
            <p className="text-sm text-muted-foreground mb-4">{t("home.coaching_desc")}</p>
            <span className="inline-flex items-center rounded-full bg-teal/15 text-teal border border-teal/30 px-4 py-1.5 text-sm font-semibold">{t("home.coming_soon")}</span>
          </div>
          </div>
        </div>
      </section>

      <section className="pb-20 px-4 sm:px-8">
        <div className="max-w-4xl mx-auto text-center bg-gradient-to-br from-teal-900/30 to-midnight-400/50 rounded-3xl p-12 sm:p-16 border border-teal/20">
          <h2 className="font-heading font-bold text-2xl sm:text-3xl tracking-tight mb-4">{t("home.cta_title")}</h2>
          <p className="text-muted-foreground mb-8 max-w-md mx-auto">{t("home.cta_subtitle")}</p>
          <Link to="/register"><Button className="rounded-full bg-coral hover:bg-coral-600 text-white px-8 py-6 text-base font-semibold" data-testid="home-register-btn">{t("home.cta_btn")}</Button></Link>
        </div>
      </section>

      <footer className="border-t border-border/50 py-8 px-4">
        <div className="max-w-7xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4 text-sm text-muted-foreground">
          <p className="font-heading font-semibold"><span className="text-golden">D&amp;D</span> Padel</p>
          <p>{club?.address}</p>
        </div>
      </footer>
    </div>
  );
}
